/**
 * Fonctions utilitaires pour les appels au worker wab-waproxy
 * Regroupe les fonctionnalités précédemment dans waproxiCalls.js et wab-config-calls/worldCalls.js
 */

import { routedFetch } from './api-router.js';
import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import { waSessionStorage } from './session-storage.js';

/**
 * Client World Anvil via WAProxy Worker
 */
const waproxyClient = {
    async waGet(theURL, theId = null, granularity = 1) {
        let endpoint = `/api/wa${theURL}`;
        const params = new URLSearchParams();
        
        if (theId) {
            params.append('id', theId);
        }
        
        if (granularity !== 1) {
            params.append('granularity', granularity);
        }
        
        if (params.toString()) {
            endpoint += '?' + params.toString();
        }
        
        // La clé API est maintenant injectée automatiquement par le routeur
        return routedFetch(endpoint, {
            method: 'GET'
        });
    },

    async waPost(theURL, theId = null, body = null) {
        const endpoint = `/api/wa${theURL}${theId ? '?id=' + theId : ''}`;
        const options = {
            method: 'POST'
        };
        
        if (body) {
            options.body = JSON.stringify(body);
        }
        
        // La clé API est maintenant injectée automatiquement par le routeur
        return routedFetch(endpoint, options);
    },

    async waPatch(theURL, theId = null, body = null) {
        const endpoint = `/api/wa${theURL}${theId ? '?id=' + theId : ''}`;
        const options = {
            method: 'PATCH'
        };
        
        if (body) {
            options.body = JSON.stringify(body);
        }
        
        // La clé API est maintenant injectée automatiquement par le routeur
        return routedFetch(endpoint, options);
    },

    /**
     * Récupère le token d'authentification en clair
     */
    async getAuthToken() {
        // Utiliser le stockage de session
        return waSessionStorage.getWAApiKey();
    },

    /**
     * Stocke le token d'authentification
     * @param {string} token - Le token à stocker
     */
    async setAuthToken(token) {
        if (token) {
            // Stocker dans le stockage de session
            waSessionStorage.setWAApiKey(token);
            debug.log(config, 'API key stored in session storage');
        } else {
            // Supprimer du stockage de session
            waSessionStorage.removeWAApiKey();
            debug.log(config, 'API key removed from session storage');
        }
    },

    /**
     * Authentifie l'utilisateur avec sa clé API
     * @param {string} apiKey - La clé API de l'utilisateur
     * @returns {Promise<{success: boolean, user?: Object, error?: string, details?: string}>}
     */
    async authenticate(apiKey) {
        if (!apiKey) {
            return { success: false, error: 'Clé API manquante' };
        }

        try {
            // S'assurer que la clé est stockée avant l'appel
            await this.setAuthToken(apiKey);
            debug.log(config, 'Clé API stockée, tentative d\'authentification');

            // Faire l'appel d'authentification
            const data = await routedFetch('/api/wa/check-access', {
                method: 'GET'
            });

            // Si l'authentification a réussi
            if (data.success === true) {
                debug.log(config, 'Authentification réussie');
                return { success: true, user: data };
            } else {
                // L'API a retourné success: false
                const errorMessage = data.error || data.message || 'Clé API invalide';
                debug.error(config, 'Échec d\'authentification:', errorMessage);
                
                // Supprimer la clé en cas d'échec
                await this.setAuthToken(null);
                
                return { 
                    success: false, 
                    error: `Authentification échouée: ${errorMessage}`,
                    details: `Réponse de l'API: ${JSON.stringify(data)}`
                };
            }

        } catch (error) {
            debug.error(config, 'Erreur lors de l\'authentification:', error);
            
            // Supprimer la clé en cas d'erreur
            await this.setAuthToken(null);
            
            return { 
                success: false, 
                error: `Impossible de se connecter au serveur d'authentification`,
                details: error.message
            };
        }
    },

    /**
     * Déconnecte l'utilisateur
     */
    logout() {
        debug.log(config, 'Déconnexion de l\'utilisateur');
        this.setAuthToken(null);
    },

    /**
     * Vérifie si l'utilisateur est authentifié
     * @returns {boolean}
     */
    isAuthenticated() {
        return !!this.getAuthToken();
    }
};

export default waproxyClient;